/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { WipEntryPoint } from '@gh/builder-shared-data';
import {
  FormStateSnapshot,
  getFormStateSnapshotValue,
  isEntityInvalid,
  markFormStateAsTouched,
  PlainImmutablePageState,
  queryRecalculationNeeded,
  setEntityFields,
  setFormStateSnapshotValue,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { FormMode } from '@gh/core-ui';
import { isNotNil } from '@gh/core-util';
import { map$ } from '@gh/rx/operators';
import { first, omit } from 'lodash';
import {
  getWipJobId,
  setWipJobField,
  validateWipMasterInfo,
  WIP_DETAILS_INIT,
  WIP_EMPTY_MASTER_INFO,
  WipApplyResponse,
  WipDetails,
  WipJob,
  wipJobArr,
  WipMasterInfo,
  WipRecalculateResponse,
  WipTab,
} from '../../resources/wip.model';

@Injectable()
export class WipFormState extends PlainImmutablePageState<WipDetails> {
  masterInfo$ = this.select(({ masterInfo }) => masterInfo);
  selectedTab$ = this.select(({ selectedTab }) => selectedTab);
  jobs$ = this.select(({ jobs }) => jobs);
  recalculationNeeded$ = this.select(queryRecalculationNeeded);
  focusedCell$ = this.select(({ focusedCell }) => focusedCell);
  isApplied$ = this.select(({ entryPoint }) => isNotNil(entryPoint));
  isProduceEntryPoint$ = this.select(({ entryPoint }) => entryPoint === WipEntryPoint.Produce);
  isViewEntryPoint$ = this.select(({ entryPoint }) => entryPoint === WipEntryPoint.View);
  isCurrentFinancialYear$ = this.select(({ isCurrentFinancialYear }) => isCurrentFinancialYear);
  masterInfoMode$ = this.isApplied$.pipe(map$((isApplied) => isApplied ? FormMode.ViewMode : FormMode.EditMode));

  constructor() {
    super(WIP_DETAILS_INIT, FormMode.EditMode);
  }

  get invalid(): boolean {
    return this.isMasterInfoInvalid || this.hasInvalidJob;
  }

  get isMasterInfoInvalid(): boolean {
    return isNotNil(validateWipMasterInfo(getFormStateSnapshotValue(this.value.masterInfo)));
  }

  get hasInvalidJob(): boolean {
    return isNotNil(this.firstInvalidJob);
  }

  get firstInvalidJob(): Maybe<WipJob> {
    return first(this.value.jobs.filter(isEntityInvalid));
  }

  get masterInfo(): WipMasterInfo {
    return getFormStateSnapshotValue(this.value.masterInfo);
  }

  build(details: WipDetails): void {
    this.setValue(details);
    this.resetDirty();
  }

  setMasterInfo(masterInfo: FormStateSnapshot<WipMasterInfo>): void {
    this.setField('masterInfo', masterInfo);
  }

  setJobField(id: ID, value?: number): void {
    this.startChangeDetection();
    this.setFieldWithDirty(
      'jobs',
      wipJobArr.update(this.value.jobs, id, (job) => setWipJobField(job, 'overrunsAdjust', value)));
    if (this.changed) {
      this.setField('isRecalculationNeeded', true);
    }
  }

  mergeOnRecalculate(recalculate: WipRecalculateResponse): void {
    this.setField(
      'jobs',
      wipJobArr.mergeWith(
        this.value.jobs,
        recalculate.journal,
        (source, { wip, wipAasb111 }) => setEntityFields(source, { wip, wipAasb111 })));
    this.setField('isRecalculationNeeded', false);
  }

  clear(): void {
    this.setFields({
      masterInfo: setFormStateSnapshotValue(
        this.value.masterInfo,
        (masterInfo) => setEntityFields(masterInfo, omit(WIP_EMPTY_MASTER_INFO, 'months'))),
      jobs: [],
      entryPoint: void 0,
    });
    this.resetDirty();
    this.setField('isRecalculationNeeded', false);
  }

  selectTab(tab: WipTab): void {
    this.setField('selectedTab', tab);
  }

  applyJobs({ entryPoint, journal, endOfTheYear }: WipApplyResponse): void {
    const jobs = journal || [];
    const firstJob = first(jobs);
    this.setFields({
      entryPoint,
      masterInfo: setFormStateSnapshotValue(
        this.value.masterInfo,
        (masterInfo) => setEntityFields(masterInfo, {
          'excludeDpp': firstJob ? firstJob.isExcludeDpp : true,
        })),
      jobs,
      endOfTheYear,
    });
    if (entryPoint === WipEntryPoint.Produce) {
      this.setDirty();
    }
  }

  highlightMasterInfo(): void {
    this.setField('masterInfo', markFormStateAsTouched(this.value.masterInfo));
  }

  focusInvalidJob(): void {
    const { firstInvalidJob } = this;

    if (firstInvalidJob) {
      this.setField('focusedCell', { id: getWipJobId(firstInvalidJob), field: 'overrunsAdjust' });
    }
  }

  resetFocusedCell(): void {
    this.setField('focusedCell', void 0);
  }
}
